import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, Clock, Users, Zap, ArrowRight, ArrowLeft, Target, Sparkles, Heart, Eye, RefreshCw } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "not-motivation", title: "ADHD Isn't a Motivation Problem" },
  { id: "long-sessions-backfire", title: "Why Long Sessions Backfire" },
  { id: "short-blocks-help", title: "Short Blocks Lower the Barrier" },
  { id: "time-awareness", title: "Time Awareness Built In" },
  { id: "breaks-prevent-overload", title: "Breaks Prevent Overload" },
  { id: "body-doubling", title: "Body Doubling and Pomodoro" },
  { id: "reduce-shame", title: "Reducing Shame Around Productivity" },
  { id: "csw-adhd", title: "Where CSW Fits In" },
  { id: "faq", title: "FAQ" },
];

const PomodoroADHD = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why does Pomodoro work for ADHD?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pomodoro works for ADHD because it removes the need to manage attention constantly. Instead of relying on internal control, the technique externalizes decisions about when to start, stop, and rest — which reduces the executive function demands that are challenging for ADHD brains."
        }
      },
      {
        "@type": "Question",
        "name": "Is ADHD a motivation problem?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. ADHD and distractibility are closely linked to difficulties with sustained attention, task initiation, time awareness, and self-regulation. Research shows these challenges are neurological and executive-function related — not about effort or intelligence."
        }
      },
      {
        "@type": "Question",
        "name": "Why do long study sessions backfire for ADHD?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unbounded study time creates too many demands — deciding when to start, stop, rest, and monitoring progress. For students with ADHD, this decision load is exhausting. External structure like Pomodoro significantly improves task persistence for individuals with attention regulation challenges."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro help with time blindness?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Many students with ADHD struggle with 'time blindness' — difficulty sensing how much time has passed. Pomodoro solves this by making time visible, creating clear start and stop points, and preventing accidental overworking. The timer becomes a support tool, not pressure."
        }
      },
      {
        "@type": "Question",
        "name": "What is body doubling and how does it help ADHD?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Body doubling means working alongside someone else. Research shows that co-presence increases focus and task persistence, even when there's no interaction. Study streams and Pomodoro-based study rooms provide this without pressure — you just need someone else working too."
        }
      },
      {
        "@type": "Question",
        "name": "How does Pomodoro reduce shame about productivity?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Many distracted students internalize the idea that they're 'bad at studying.' Pomodoro reframes effort as showing up, doing one block, taking breaks, and repeating. Research shows that small, repeatable wins improve confidence and persistence, especially in students who've struggled before."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Pomodoro for ADHD and Easily Distracted Students",
    "description": "Learn how the Pomodoro Technique supports students with ADHD through external structure, time awareness, body doubling, and reduced executive function demands.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/adhd/"
    },
    "keywords": ["pomodoro technique", "ADHD", "ADHD study tips", "focus ADHD", "body doubling", "time blindness", "executive function", "ADHD productivity", "study stream", "online workspace", "virtual study room", "focus timer", "distraction management", "study with me", "online study platform", "neurodivergent studying"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pomodoro for ADHD and Easily Distracted Students | CSW",
    "description": "Learn how the Pomodoro Technique supports students with ADHD through external structure, time awareness, and body doubling.",
    "url": "https://csw.live/pomodoro-technique/adhd/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "ADHD Study Strategies"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "ADHD" },
      { "@type": "Thing", "name": "Body Doubling" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Time Blindness" },
      { "@type": "Thing", "name": "Executive Function" },
      { "@type": "Thing", "name": "Distraction Management" },
      { "@type": "Thing", "name": "Neurodivergent Studying" },
      { "@type": "Thing", "name": "External Structure" }
    ],
    "keywords": "pomodoro technique, ADHD, ADHD study tips, body doubling, study stream, online workspace, virtual study room, focus timer, time blindness, executive function, neurodivergent studying"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "ADHD",
        "item": "https://csw.live/pomodoro-technique/adhd/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Customize Your Pomodoro",
      path: "/pomodoro-technique/customize/",
      description: "Finding the right session length for you"
    },
    {
      title: "Pomodoro vs. Procrastination",
      path: "/pomodoro-technique/pomodoro-vs-procrastination/",
      description: "How short sessions make starting easier"
    },
    {
      title: "Why Breaks Make You Smarter",
      path: "/pomodoro-technique/why-breaks-matter/",
      description: "The psychology behind Pomodoro breaks"
    },
    {
      title: "Pomodoro and Study Streams",
      path: "/pomodoro-technique/study-streams/",
      description: "Why studying together quietly improves focus"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Pomodoro for ADHD and Easily Distracted Students | CSW"
        description="Learn how the Pomodoro Technique supports students with ADHD through external structure, time awareness, body doubling, and reduced executive function demands."
        canonicalUrl="/pomodoro-technique/adhd/"
      />
      <Header />
      <BlogReadingProgress />
      
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">ADHD</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Brain className="w-4 h-4" />
                <span>ADHD Support</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Pomodoro for{" "}
                <span className="text-primary">ADHD</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Why structure helps when focus feels impossible.
              </p>
            </div>
          </div>
        </section>

        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              <div className="flex-1 max-w-3xl">
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  If you've ever tried to study and felt like your brain just <em>won't stay put</em>, you're not broken — and you're definitely not alone.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Students with ADHD or high distractibility often hear the same advice: "Just focus harder."
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  That advice doesn't help.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  The Pomodoro Technique works for many distracted students <strong>not because it fixes attention</strong>, but because it <strong>removes the need to manage attention constantly</strong>.
                </p>

                {/* ADHD Isn't a Motivation Problem */}
                <div id="not-motivation" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        ADHD and Distraction Aren't Motivation Problems
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  ADHD and distractibility are closely linked to difficulties with:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>sustained attention</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>task initiation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>time awareness</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>self-regulation</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows these challenges are <strong>neurological and executive-function related</strong> — not about effort or intelligence (
                  <a href="https://psycnet.apa.org/record/2011-29683-000" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Barkley, 2012</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is why long, unstructured study sessions are especially difficult. Many students with ADHD find they need to <Link to="/pomodoro-technique/customize/" className="text-primary hover:underline">customize their Pomodoro timing</Link> — often using shorter sessions like 20/5 or 15/5.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro changes the structure, not the student.
                  </p>
                </div>

                {/* Why Long Sessions Backfire */}
                <div id="long-sessions-backfire" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Long Study Sessions Backfire for Distracted Brains
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Unbounded study time creates too many demands:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>deciding when to start</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>deciding when to stop</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>deciding when to rest</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>monitoring progress</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  For students with ADHD, this decision load is exhausting.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on executive function shows that <strong>external structure significantly improves task persistence</strong> for individuals with attention regulation challenges.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro externalizes those decisions.
                  </p>
                </div>

                {/* Short Blocks Lower the Barrier */}
                <div id="short-blocks-help" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Short Focus Blocks Lower the Barrier to Starting
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Starting is often the hardest part.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro reframes studying as:
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-8">
                  <p className="text-xl font-display font-semibold text-foreground text-center m-0">
                    "Focus for 25 minutes"
                  </p>
                  <p className="text-lg font-body text-muted-foreground text-center mt-2 mb-0">
                    instead of "Sit and study for hours."
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Behavioral research shows that <strong>short, time-limited commitments increase task initiation</strong>, especially for individuals prone to avoidance (
                  <a href="https://psycnet.apa.org/record/2007-00tried-001" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Steel, 2007</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    For distracted students, that difference is everything.
                  </p>
                </div>

                {/* Time Awareness Built In */}
                <div id="time-awareness" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Time Awareness Is Built Into Pomodoro
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Many students with ADHD struggle with <em>time blindness</em> — difficulty sensing how much time has passed.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro solves this by:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>making time visible</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>creating clear start and stop points</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>preventing accidental overworking</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Clinical research shows that <strong>external time cues improve self-regulation and task completion</strong> in ADHD populations.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    The timer becomes a support tool, not pressure.
                  </p>
                </div>

                {/* Breaks Prevent Overload */}
                <div id="breaks-prevent-overload" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Breaks Prevent Overload and Emotional Shutdown
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Distracted brains fatigue faster.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro's frequent breaks:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>prevent cognitive overload</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>reduce emotional frustration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>lower the risk of quitting entirely</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on ADHD and cognitive fatigue shows that <strong>regular breaks improve persistence and reduce task avoidance</strong>.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Skipping breaks often leads to shutdown, not productivity.
                  </p>
                </div>

                {/* Body Doubling and Pomodoro */}
                <div id="body-doubling" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Body Doubling: Why Studying With Others Helps ADHD Focus
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  One of the most effective strategies for ADHD is <strong>body doubling</strong> — working alongside someone else.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Study streams and Pomodoro-based study rooms provide this without pressure.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows that <strong>co-presence increases focus and task persistence</strong>, even when there's no interaction.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  You don't need conversation.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    You just need someone else working too.
                  </p>
                </div>

                {/* Reducing Shame Around Productivity */}
                <div id="reduce-shame" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Pomodoro Helps Reduce Shame Around Productivity
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Many distracted students internalize the idea that they're "bad at studying."
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro reframes effort as:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>showing up</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>doing one block</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>taking breaks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>repeating</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on self-efficacy shows that <strong>small, repeatable wins improve confidence and persistence</strong>, especially in students who've struggled before.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    That emotional shift matters.
                  </p>
                </div>

                {/* Where CSW Fits In */}
                <div id="csw-adhd" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Where CSW Supports Distracted Students
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is where <strong>csw.live</strong> fits naturally.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  CSW offers:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <Timer className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>Pomodoro-based study sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>silent camera-on rooms (body doubling)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>shared focus without social pressure</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Instead of fighting distraction alone, students get structure and presence built in.
                </p>

                {/* Final Takeaway */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12">
                  <h3 className="text-xl font-display font-bold mb-4">Final Takeaway</h3>
                  <p className="text-lg font-body text-foreground/90 mb-0">
                    Pomodoro doesn't cure ADHD. It <strong>supports it</strong>. By externalizing time, boundaries, and structure, Pomodoro removes the constant negotiation with focus — and gives distracted brains a framework that actually works.
                  </p>
                </div>

                {/* FAQ Section */}
                <div id="faq" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        Frequently Asked Questions
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="border-b border-border pb-6 last:border-0 last:pb-0">
                        <h3 className="text-lg font-display font-semibold mb-2">{faq.name}</h3>
                        <p className="text-foreground/80 font-body">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center py-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Pomodoro Technique</span>
                  </Link>
                  <Link 
                    to="/study-timer/" 
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <span>Try the Pomodoro Timer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Articles */}
                <div className="mt-16">
                  <h2 className="text-2xl font-display font-bold mb-8">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default PomodoroADHD;
